import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@kgc/i18n';
import { Badge, cn } from '@kgc/ui';
import { AlertTriangle, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import {
  caseIndex,
  caseToFeature,
  type DaforgeCase,
  type DaforgeChange,
  type DaforgeRelease,
  daforgeVersion,
  INTERNAL_AUDIENCE,
} from '../data/daforge-data';
import { type TestResultData, useTestResults } from '../hooks/useTestResults';
import { downloadCsv } from '../utils/csv-export';
import { generateDaforgeCsv } from '../utils/daforge-csv';
import { TestFeatureCard } from './TestFeatureCard';

export interface DaforgeFilters {
  area: string;
  onlyOpen: boolean;
  showInternal: boolean;
}

interface DaforgeReleaseGroupProps {
  release: DaforgeRelease;
  filters: DaforgeFilters;
  isOpen: boolean;
  onToggle: () => void;
  isChangeOpen: (changeId: string) => boolean;
  onToggleChange: (changeId: string) => void;
}

type CaseSlot = { mine?: TestResultData; others: TestResultData[] };

const isDone = (r: TestResultData | undefined) => r?.status === 'passed' || r?.status === 'failed';

/**
 * Egy DaForge-kiadás összecsukható csoportja. A kiadás SAJÁT version-kulcsán kérdez
 * (`daforge-<id>`), így a kiadások eredményei nem keverednek. A kártyán a SAJÁT eredmény
 * szerkeszthető; a többi tesztelőé egy sorban látszik.
 */
export function DaforgeReleaseGroup({
  release: rel,
  filters,
  isOpen,
  onToggle,
  isChangeOpen,
  onToggleChange,
}: DaforgeReleaseGroupProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const version = daforgeVersion(rel.release.id);
  const { data: results } = useTestResults(version);
  const rid = rel.release.id;

  // featureIndex → { mine, others } — a táblában tesztelőnként egy sor van.
  const byCase = useMemo(() => {
    const map = new Map<number, CaseSlot>();
    for (const r of results ?? []) {
      const slot = map.get(r.featureIndex) ?? { others: [] };
      if (user && r.testerId === user.id) slot.mine = r;
      else slot.others.push(r);
      map.set(r.featureIndex, slot);
    }
    return map;
  }, [results, user]);

  const casesByChange = useMemo(() => {
    const map = new Map<string, DaforgeCase[]>();
    for (const c of rel.cases) map.set(c.change, [...(map.get(c.change) ?? []), c]);
    for (const list of map.values()) {
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id));
    }
    return map;
  }, [rel.cases]);

  // Stabil kártya-bemenet: a kártya `useMemo`-i a `feature` referenciáján állnak.
  const featureByCase = useMemo(() => {
    const changeById = new Map(rel.changes.map(c => [c.id, c]));
    const map = new Map<string, ReturnType<typeof caseToFeature>>();
    for (const c of rel.cases) {
      const ch = changeById.get(c.change);
      if (ch) map.set(c.id, caseToFeature(c, ch));
    }
    return map;
  }, [rel.changes, rel.cases]);

  const visibleChanges = useMemo(
    () => rel.changes.filter(c => filters.showInternal || c.audience !== INTERNAL_AUDIENCE),
    [rel.changes, filters.showInternal]
  );

  const stat = useCallback(
    (cases: DaforgeCase[]) => {
      let mine = 0;
      let failed = 0;
      for (const c of cases) {
        const slot = byCase.get(caseIndex(c.id));
        if (isDone(slot?.mine)) mine++;
        if (slot?.mine?.status === 'failed' || slot?.others.some(r => r.status === 'failed'))
          failed++;
      }
      return { mine, failed, total: cases.length };
    },
    [byCase]
  );

  const totals = useMemo(
    () => stat(visibleChanges.flatMap(c => casesByChange.get(c.id) ?? [])),
    [stat, visibleChanges, casesByChange]
  );

  const shown = useMemo(() => {
    let list = visibleChanges.filter(c => !filters.area || c.area === filters.area);
    if (filters.onlyOpen) {
      list = list.filter(c =>
        (casesByChange.get(c.id) ?? []).some(tc => !isDone(byCase.get(caseIndex(tc.id))?.mine))
      );
    }
    return [...list].sort((a, b) => b.date.localeCompare(a.date) || a.id.localeCompare(b.id));
  }, [visibleChanges, filters.area, filters.onlyOpen, casesByChange, byCase]);

  const byDay = useMemo(() => {
    const map = new Map<string, DaforgeChange[]>();
    for (const c of shown) map.set(c.date, [...(map.get(c.date) ?? []), c]);
    return [...map.entries()];
  }, [shown]);

  const handleExport = useCallback(() => {
    downloadCsv(generateDaforgeCsv(rel, results ?? []), `daforge-tesztjegyzokonyv-${rid}.csv`);
  }, [rel, results, rid]);

  const pct = totals.total ? Math.round((totals.mine / totals.total) * 100) : 0;
  const Chevron = isOpen ? ChevronDown : ChevronRight;

  return (
    <section data-testid={`daforge-release-${rid}`}>
      {/* Kiadás-fej — kattintásra nyit/csuk */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3.5">
        <button
          onClick={onToggle}
          aria-expanded={isOpen}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <Chevron className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0">
            <span className="block font-semibold">{rel.release.title}</span>
            <span className="block text-xs text-muted-foreground">
              {t('releaseHub.daforge.period', { from: rel.release.from, to: rel.release.to })}
            </span>
          </span>
          <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300">
            {rel.release.status}
          </Badge>
        </button>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {totals.failed > 0 && (
            <span className="font-semibold text-red-600 dark:text-red-400">
              {t('releaseHub.daforge.failedCount', { count: totals.failed })}
            </span>
          )}
          <span data-testid={`daforge-progress-${rid}`}>
            {t('releaseHub.daforge.myProgress', { done: totals.mine, total: totals.total })}
          </span>
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }} />
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-1 rounded px-2 py-1 hover:bg-accent hover:text-foreground"
          >
            <Download className="h-3.5 w-3.5" />
            {t('releaseHub.daforge.exportCsv')}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-1 space-y-3 rounded-b-lg border border-t-0 border-border bg-card p-3">
          {shown.length === 0 && (
            <p className="py-6 text-center text-muted-foreground">
              {t('releaseHub.daforge.noChanges')}
            </p>
          )}
          {byDay.map(([day, changes]) => (
            <div key={day} className="space-y-2">
              <h3 className="text-xs font-semibold text-muted-foreground">{day}</h3>
              {changes.map(ch => {
                const cases = casesByChange.get(ch.id) ?? [];
                const s = stat(cases);
                const open = isChangeOpen(ch.id);
                const ChChevron = open ? ChevronDown : ChevronRight;
                return (
                  <article
                    key={ch.id}
                    data-testid={`daforge-change-${ch.id}`}
                    className="rounded-lg border border-border"
                  >
                    {/* Csukott sor: típus · cím · terület · saját haladás · jelzések */}
                    <button
                      onClick={() => onToggleChange(ch.id)}
                      aria-expanded={open}
                      data-testid={`daforge-change-toggle-${ch.id}`}
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-accent/40"
                    >
                      <ChChevron className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <Badge className="shrink-0 bg-accent text-foreground">{ch.kind}</Badge>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {ch.title}
                      </span>
                      <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
                        {ch.area}
                      </span>
                      {ch.uncertain && (
                        <AlertTriangle
                          className="h-4 w-4 shrink-0 text-amber-500"
                          aria-label={t('releaseHub.daforge.uncertain')}
                        />
                      )}
                      {s.failed > 0 && (
                        <span
                          className="shrink-0 text-xs font-semibold text-red-600 dark:text-red-400"
                          aria-label={t('releaseHub.daforge.failedCount', { count: s.failed })}
                        >
                          ✗
                        </span>
                      )}
                      {s.total > 0 && (
                        <span
                          className={cn(
                            'shrink-0 text-xs tabular-nums',
                            s.mine === s.total
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-muted-foreground'
                          )}
                          data-testid={`daforge-change-progress-${ch.id}`}
                        >
                          {s.mine}/{s.total}
                        </span>
                      )}
                    </button>

                    {open && (
                      <div className="space-y-2 border-t border-border p-3">
                        <div className="text-xs text-muted-foreground">
                          {ch.area} · {ch.audience} · {ch.id}
                        </div>
                        <p className="text-sm text-muted-foreground">{ch.summary}</p>
                        {ch.uncertain && (
                          <div className="flex gap-2 rounded border-l-[3px] border-amber-500 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                              <strong>{t('releaseHub.daforge.uncertain')}</strong>
                              {ch.note ? ` — ${ch.note}` : ''}
                            </span>
                          </div>
                        )}
                        {cases.map(tc => {
                          const idx = caseIndex(tc.id);
                          const slot = byCase.get(idx);
                          const feature = featureByCase.get(tc.id);
                          if (!feature) return null;
                          return (
                            <div key={tc.id}>
                              <TestFeatureCard
                                feature={feature}
                                featureIndex={idx}
                                version={version}
                                existingResult={slot?.mine}
                              />
                              {slot && slot.others.length > 0 && (
                                <div
                                  className="mt-1 flex flex-wrap gap-x-3 px-1 text-xs text-muted-foreground"
                                  data-testid={`daforge-others-${tc.id}`}
                                >
                                  <span>{t('releaseHub.daforge.others')}:</span>
                                  {slot.others.map(r => (
                                    <span
                                      key={r.id}
                                      className={cn(
                                        r.status === 'passed' &&
                                          'text-green-600 dark:text-green-400',
                                        r.status === 'failed' && 'text-red-600 dark:text-red-400'
                                      )}
                                    >
                                      {r.status === 'passed'
                                        ? '✓'
                                        : r.status === 'failed'
                                          ? '✗'
                                          : '–'}{' '}
                                      {r.testerName}
                                      {r.comment ? ` („${r.comment}”)` : ''}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
